import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Menu from '../sections/Menu';
import Footer from '../sections/Footer';
import { Editor } from '@tinymce/tinymce-react';
import PageMeta from '../seo/PageMeta';
import {
  BLOG_CATEGORIES,
  getCategoryMetaByName,
  serializePostCategories,
  splitPostCategories,
} from '../blog/categories';
import { getPostThumbnail } from '../blog/media';
import { slugify } from '../blog/slug';

const allowedEmails = [
  'iwona.bendig@egodziny.pl',
  'krzysztof.warda@egodziny.pl',
  'dominik.pogorzelski@egodziny.pl'
];

const toJsDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatDateForInput = (value) => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return trimmedValue;
    }
  }

  const date = toJsDate(value);
  if (!date) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizePostForm = (postData = {}) => ({
  title: postData.title || '',
  slug: postData.slug || '',
  content: postData.content || '',
  author: postData.author || '',
  author_email: postData.author_email || '',
  category: serializePostCategories(splitPostCategories(postData.category)),
  thumbnail: getPostThumbnail(postData),
  publish_date: formatDateForInput(postData.date || postData.publish_date),
  reading_time: postData.reading_time || '',
  meta_description: postData.meta_description || '',
  public: typeof postData.public === 'boolean' ? postData.public : true,
});

export default function EditBlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(
    normalizePostForm({
      publish_date: formatDateForInput(new Date()),
    })
  );
  const [loading, setLoading] = useState(true);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const user = getAuth().currentUser;

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    if (!cloudName || !uploadPreset) {
      alert('Brak konfiguracji Cloudinary. Ustaw REACT_APP_CLOUDINARY_CLOUD_NAME i REACT_APP_CLOUDINARY_UPLOAD_PRESET w .env');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('upload_preset', uploadPreset);
      data.append('folder', 'blog-thumbnails');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        { method: 'POST', body: data }
      );

      if (!response.ok) {
        throw new Error(`Cloudinary upload failed: ${response.status}`);
      }

      const result = await response.json();
      setForm((prev) => ({ ...prev, thumbnail: result.secure_url }));
    } catch (error) {
      console.error(error);
      alert('Nie udało się wgrać zdjęcia. Spróbuj ponownie.');
    } finally {
      setUploadingThumbnail(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (!user || !allowedEmails.includes(user.email)) {
      alert('Brak dostępu');
      navigate('/');
      return;
    }
    async function fetchPost() {
      if (id !== 'new') {
        const docRef = doc(db, 'blogs', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setForm({ ...normalizePostForm(docSnap.data()), slug: id });
        }
      } else {
        setForm((prev) =>
          normalizePostForm({
            ...prev,
            author_email: user.email,
            publish_date: prev.publish_date || formatDateForInput(new Date()),
          })
        );
      }
      setLoading(false);
    }
    fetchPost();
  }, [id, user, navigate]);

  useEffect(() => {
    if (!form.thumbnail && form.content?.includes('<img')) {
      const parser = new DOMParser();
      const docContent = parser.parseFromString(form.content, 'text/html');
      const firstImage = docContent.querySelector('img');
      if (firstImage?.src) {
        setForm((prev) => ({
          ...prev,
          thumbnail: firstImage.src,
        }));
      }
    }
  }, [form.content, form.thumbnail]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const selectedCategoryIds = new Set(
    splitPostCategories(form.category)
      .map((categoryName) => getCategoryMetaByName(categoryName)?.id || String(categoryName).trim().toLowerCase())
  );

  const handleCategoryToggle = (category) => {
    const currentCategories = splitPostCategories(form.category);

    const hasCategory = currentCategories.some((categoryName) => {
      const categoryMeta = getCategoryMetaByName(categoryName);
      return categoryMeta ? categoryMeta.id === category.id : String(categoryName).trim().toLowerCase() === category.id;
    });

    const nextCategories = hasCategory
      ? currentCategories.filter((categoryName) => {
          const categoryMeta = getCategoryMetaByName(categoryName);
          return categoryMeta ? categoryMeta.id !== category.id : String(categoryName).trim().toLowerCase() !== category.id;
        })
      : [...currentCategories, category.name];

    setForm((prevForm) => ({
      ...prevForm,
      category: serializePostCategories(nextCategories),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const postId = id === 'new'
      ? (slugify(form.slug) || slugify(form.title))
      : (slugify(form.slug) || id);
    const slugChanged = id !== 'new' && postId !== id;

    if (!postId) {
      alert('Nie udało się wygenerować adresu URL — podaj tytuł lub własny slug');
      return;
    }

    if (slugChanged) {
      const ok = window.confirm(
        `Zmieniasz adres posta z „/blog/${id}" na „/blog/${postId}".\n\n` +
          'Stary adres przestanie działać (linki w Google/zewnętrzne mogą się zepsuć). ' +
          'Pamiętaj o przebudowaniu i wdrożeniu projektu. Kontynuować?'
      );
      if (!ok) return;
    }

    if (!form.meta_description.trim()) {
      alert('Uzupełnij meta description (opis dla Google) — to ważne dla SEO');
      return;
    }

    const normalizedForm = normalizePostForm(form);
    const parsedPublishDate = new Date(`${normalizedForm.publish_date}T12:00:00`);
    const postPayload = { ...normalizedForm };
    delete postPayload.publish_date;
    // `slug` is a UI-only field — the slug IS the Firestore document id.
    delete postPayload.slug;

    if (Number.isNaN(parsedPublishDate.getTime())) {
      alert('Nieprawidłowa data publikacji');
      return;
    }

    if (id === 'new' || slugChanged) {
      const existing = await getDoc(doc(db, 'blogs', postId));
      if (existing.exists()) {
        alert(`Post o adresie „/blog/${postId}" już istnieje — wybierz inny własny URL (slug).`);
        return;
      }
    }

    await setDoc(doc(db, 'blogs', postId), {
      ...postPayload,
      date: parsedPublishDate,
    });

    if (slugChanged) {
      await deleteDoc(doc(db, 'blogs', id));
    }

    navigate(`/blog/${postId}`);
  };

  if (loading) return <p></p>;

  return (
    <>
      <PageMeta path="/edit/:id" />
      <Menu />
      <main style={{ padding: '2rem' }}>
        <h1>{id === 'new' ? 'Nowy post' : 'Edytuj post'}</h1>
        <form className='blog-post-edit'  onSubmit={handleSubmit}>
          <input type="text" name="title" placeholder="Tytuł" value={form.title} onChange={handleChange} required />
          <br /><br />
          <label className="blog-post-field-label" htmlFor="slug">
            Własny URL (slug)
            {id !== 'new' ? (
              <span
                className="blog-post-slug-warning"
                title={
                  'Uwaga: zmiana sluga zmienia adres posta. Stary adres /blog/' +
                  id +
                  ' przestanie działać — linki w Google i zewnętrzne mogą się zepsuć. ' +
                  'Po zapisie przebuduj i wdróż projekt (npm run build + deploy).'
                }
              >
                {' '}⚠️
              </span>
            ) : null}
          </label>
          <p className="blog-post-field-helper">
            {id === 'new'
              ? 'Zostaw puste, aby wygenerować z tytułu. Dozwolone: małe litery, cyfry i myślniki.'
              : 'Zmiana zmieni adres posta — najedź na ⚠️, by zobaczyć skutki. Po zapisie przebuduj projekt.'}
          </p>
          <input
            id="slug"
            type="text"
            name="slug"
            placeholder="np. doba-pracownicza-co-to-jest"
            value={form.slug}
            onChange={handleChange}
          />
          <p className="blog-post-field-helper">
            Adres posta: <code>/blog/{slugify(form.slug) || slugify(form.title) || '…'}</code>
          </p>
          <br /><br />
          <input type="text" name="author" placeholder="Autor" value={form.author} onChange={handleChange} required />
          <br /><br />
          <label className="blog-post-field-label" htmlFor="publish_date">
            Data publikacji
          </label>
          <input
            id="publish_date"
            type="date"
            name="publish_date"
            value={form.publish_date}
            onChange={handleChange}
            required
          />
          <br /><br />
          <div className="blog-post-categories-picker">
            <p className="blog-post-field-label">Kategorie posta</p>
            <p className="blog-post-field-helper">Możesz wybrać kilka kategorii.</p>
            <div className="blog-post-categories-grid">
              {BLOG_CATEGORIES.map((category) => {
                const isSelected = selectedCategoryIds.has(category.id);

                return (
                  <label
                    key={category.id}
                    className={`blog-post-category-option ${isSelected ? 'is-selected' : ''}`}
                    style={{
                      '--category-color': category.color,
                      '--category-bg': category.bgColor,
                      '--category-border': category.borderColor,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCategoryToggle(category)}
                    />
                    <span className="blog-post-category-name">{category.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <br /><br />
          <label className="blog-post-field-label" htmlFor="thumbnail-upload">
            Miniatura posta
          </label>
          <input
            id="thumbnail-upload"
            type="file"
            accept="image/*"
            onChange={handleThumbnailUpload}
            disabled={uploadingThumbnail}
          />
          {uploadingThumbnail ? <p>Wgrywam zdjęcie…</p> : null}
          <br />
          <input
            type="text"
            name="thumbnail"
            placeholder="…lub wklej URL miniatury"
            value={form.thumbnail}
            onChange={handleChange}
          />
          {form.thumbnail ? (
            <>
              <br />
              <img
                src={form.thumbnail}
                alt="Podgląd miniatury posta"
                className="blog-post-thumbnail-preview"
              />
            </>
          ) : null}
          <br /><br />
          <input type="text" name="reading_time" placeholder="Czas czytania" value={form.reading_time} onChange={handleChange} />
          <br /><br />
          <label className="blog-post-field-label" htmlFor="meta_description">
            Meta description (opis dla Google)
          </label>
          <input
            id="meta_description"
            type="text"
            name="meta_description"
            placeholder="Krótki opis posta wyświetlany w wynikach Google"
            value={form.meta_description}
            onChange={handleChange}
            required
          />
          <p
            className="blog-post-field-helper"
            style={{
              color:
                form.meta_description.length === 0
                  ? '#dc2626'
                  : form.meta_description.length < 50 || form.meta_description.length > 160
                  ? '#d97706'
                  : '#16a34a',
            }}
          >
            {form.meta_description.length} znaków · zalecane 150–160
          </p>
          <br /><br />
          <Editor
            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
            textareaName='Body'
            value={form.content}
            init={{
              height: 500,
              menubar: true,
              relative_urls: false,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
                'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
              ],
              toolbar:
                'undo redo | blocks | bold italic underline forecolor backcolor | ' +
                'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
                'link image | removeformat | help',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              paste_data_images: true,
            }}
            onEditorChange={(newText) => setForm({ ...form, content: newText })}
          />
          <br /><br />
          <label>
            <input type="checkbox" name="public" checked={form.public} onChange={handleChange} />
            &nbsp;Publiczny post (widoczny dla wszystkich)
          </label>
          <br /><br />
          <button className='button-default btn' type="submit">Zapisz</button>
        </form>
      </main>
      <Footer />
    </>
  );
}
